<template lang="pug">
	//- Голосовой ввод: состояние режима постоянного прослушивания, запись по зажатой кнопке и режим прослушивания с ключевыми фразами.
	//- Между состоянием и кнопками микрофона выводятся кнопки, переданные в слот (выбор файлов, реагирование на ошибки превью)
	.ai-chat-voice-controls
		//- Состояние режима постоянного прослушивания микрофона
		.ai-chat-voice-status(v-if="listeningState !== 'off'" :class="listeningState")
			i.fa(:class="listeningState === 'recording' ? 'fa-circle' : 'fa-circle-o'")
			span {{getListeningStatusText()}}
		slot
		//- 1 подход: запись пока кнопка зажата, отправка по отпусканию
		button.btn.btn-sm.voice-button.push-to-talk-button(type="button" :class="{recording: pushToTalkRecording}" :disabled="listeningState !== 'off' || !voiceInputSupported" v-on:mousedown="startPushToTalk($event)" v-on:touchstart.prevent="startPushToTalk($event)" v-on:contextmenu.prevent v-b-tooltip.hover="{delay: { show: 500, hide: 100 }, title: 'Hold to record a voice message'}")
			i.fa.fa-microphone
		//- 2 подход: режим постоянного прослушивания с ключевыми фразами (вкл/выкл)
		button.btn.btn-sm.voice-button.wake-word-button(type="button" :class="{active: listeningState !== 'off', recording: listeningState === 'recording'}" :disabled="pushToTalkRecording || !voiceInputSupported" v-on:click="toggleWakeWordMode()" v-b-tooltip.hover="{delay: { show: 500, hide: 100 }, title: listeningState === 'off' ? 'Enable voice activation (start/stop phrases)' : 'Disable voice activation'}")
			i.fa(:class="listeningState === 'loading' ? 'fa-spinner fa-spin' : 'fa-assistive-listening-systems'")
</template>

<script>
	import { Version } 			from './../../services/Version'
	import { AIAgent } 			from './../../services/AIAgent'
	import { Notifications } 	from './../../services/Notifications'

	// Слишком короткое нажатие кнопки микрофона (мс) считаем случайным - запись не отправляем
	const MIN_PUSH_TO_TALK_DURATION = 300;
	// Тексты состояний режима постоянного прослушивания
	const LISTENING_STATUS_TEXTS = {
		loading: 'Loading voice model...',
		waiting: 'Say the start phrase',
		recording: 'Recording... say the stop phrase or pause',
		sending: 'Sending voice message...'
	};

	export default {
		name: "AIAgentVoiceControls",
		// chat - текущий чат (null если чатов в версии ещё нет), modelCodename - выбранная модель
		props: ['chat', 'modelCodename'],
		data() {

			return {
				// Идёт запись по зажатой кнопке микрофона
				pushToTalkRecording: false,
				// Состояние режима постоянного прослушивания: 'off' | 'loading' | 'waiting' | 'recording' | 'sending'
				listeningState: AIAgent.getListeningState(),
				voiceInputSupported: AIAgent.isVoiceInputSupported()
			};

		},
		created() {

			// Состояние режима постоянного прослушивания микрофона
			Broadcast.on("AI Chat Voice Listening State Changed", state => {

				this.listeningState = state;

			}, "vue-component-" + this._uid + "-" + this.$options.name);

		},
		beforeDestroy() {

			Broadcast.off(["AI Chat Voice Listening State Changed"], "vue-component-" + this._uid + "-" + this.$options.name);

			this.stopPushToTalk(true);

		},
		methods: {
			getListeningStatusText() {

				return LISTENING_STATUS_TEXTS[this.listeningState] || '';

			},
			// 1 подход: начинаем запись по нажатию кнопки микрофона, отпускание ловим на window, чтобы запись останавливалась даже если курсор ушёл с кнопки
			startPushToTalk(e) {

				if (e.type === 'mousedown' && e.button !== 0) return;
				if (this.pushToTalkRecording || !Version.SelectedVersion || AIAgent.isWakeWordModeEnabled()) return;

				this.pushToTalkRecording = true;
				this.pushToTalkStartTime = Date.now();

				this.onPushToTalkStop = () => this.stopPushToTalk();

				window.addEventListener('mouseup', this.onPushToTalkStop);
				window.addEventListener('touchend', this.onPushToTalkStop);
				window.addEventListener('touchcancel', this.onPushToTalkStop);

				AIAgent.startRecording().catch(e => {

					this.stopPushToTalk(true);

					Notifications.showError(`Microphone is not available: ${e?.message || e}`);

				});

			},
			// Отпускание кнопки микрофона: останавливаем запись и отправляем аудио на сервер (cancel - отбросить запись без отправки)
			async stopPushToTalk(cancel = false) {

				if (!this.pushToTalkRecording) return;

				window.removeEventListener('mouseup', this.onPushToTalkStop);
				window.removeEventListener('touchend', this.onPushToTalkStop);
				window.removeEventListener('touchcancel', this.onPushToTalkStop);

				this.pushToTalkRecording = false;

				let versionId = Version.SelectedVersion?.id,
					chatId = this.chat ? this.chat.id : null,
					duration = Date.now() - this.pushToTalkStartTime;

				let blob = await AIAgent.stopRecording();

				// Слишком короткое нажатие - случайный клик, запись не отправляем
				if (cancel || !blob || !versionId || duration < MIN_PUSH_TO_TALK_DURATION) return;

				AIAgent.sendVoiceMessage(blob, versionId, chatId, this.modelCodename);

			},
			// 2 подход: включение/выключение режима постоянного прослушивания. Если фразы ещё не записаны - открываем окно обучения
			toggleWakeWordMode() {

				if (AIAgent.isWakeWordModeEnabled()) return AIAgent.stopWakeWordMode();

				if (!AIAgent.hasVoiceModel()) return Broadcast.call('Show AIAgentVoiceTraining');

				// Целевой чат определяется в момент отправки записи - пользователь мог переключить чат или версию пока режим включён
				AIAgent.startWakeWordMode(() => ({versionId: Version.SelectedVersion?.id, chatId: this.chat ? this.chat.id : null, model: this.modelCodename})).catch(e => {

					Notifications.showError(`Voice activation failed: ${e?.message || e}`);

				});

			}
		}
	}
</script>

<style lang="sass">

	.ai-chat
		.ai-chat-input
			.ai-chat-controls
				.ai-chat-voice-controls
					display: flex
					flex-direction: row
					align-items: center

				// Состояние режима постоянного прослушивания слева от кнопок голосового ввода
				.ai-chat-voice-status
					display: flex
					flex-direction: row
					align-items: center
					gap: 6px
					margin-right: 10px
					font-size: 12px
					color: #adb6bf
					white-space: nowrap
					cursor: default

					i.fa
						font-size: 10px
						color: #20a8d8

					&.recording
						color: #f3f4f5

						i.fa
							color: #f86c6b
							animation: ai-chat-voice-pulse 1s ease-in-out infinite

	@keyframes ai-chat-voice-pulse
		0%
			opacity: 1
		50%
			opacity: 0.55
		100%
			opacity: 1

</style>
